import { useState, useEffect } from "react";
import { X, Loader2, CheckCircle2, CalendarDays, Tag, UserPlus } from "lucide-react";
import { Link } from "react-router-dom";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  formatRupiah, formatDate, addPeriod,
  DURATION_UNIT_LABELS, DURATION_UNIT_PRICE_KEY,
} from "@/components/booking/bookingMeta";

const UNIT_OPTIONS = [
  { value: "day", label: "Hari" },
  { value: "month", label: "Bulan" },
  { value: "year", label: "Tahun" },
];

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export default function BookingModal({ open, room, availableRooms = [], propertyId, propertyName, initialPromoCode, initialUnit = "month", onClose, onSuccess }) {
  const { user, loginWithToken } = useAuth();
  const guestMode = !user;
  const [startDate, setStartDate] = useState(todayISO());
  const [duration, setDuration] = useState(1);
  const [unit, setUnit] = useState(initialUnit);
  const [notes, setNotes] = useState("");
  const [roomId, setRoomId] = useState(room?.id);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [emailExists, setEmailExists] = useState(false);
  const [result, setResult] = useState(null);
  // Guest identity (only when not logged in)
  const [gName, setGName] = useState("");
  const [gPhone, setGPhone] = useState("");
  const [gEmail, setGEmail] = useState("");
  // Promo / discount (Step 25)
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);        // validated result from backend
  const [promoError, setPromoError] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);

  // The room being booked. When multiple available rooms are passed (e.g. from the
  // sidebar "Booking Kamar"), the user can switch rooms via a dropdown.
  const roomChoices = availableRooms.length ? availableRooms : (room ? [room] : []);
  const activeRoom = roomChoices.find((r) => r.id === roomId) || room || roomChoices[0];

  // Sync selected unit + room to the room the booking was opened for.
  useEffect(() => {
    if (open && room) { setUnit(initialUnit); setDuration(1); setRoomId(room.id); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, room?.id, initialUnit]);

  // Auto-apply a promo code passed in from the "Gunakan Promo" discovery button.
  useEffect(() => {
    if (open && room && initialPromoCode) {
      setPromoInput(initialPromoCode);
      apiClient.post("/promos/validate", {
        property_id: propertyId, room_id: room.id,
        promo_code: initialPromoCode, duration: 1, duration_unit: initialUnit,
      }).then(({ data }) => setPromo(data)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialPromoCode, room?.id]);

  if (!open || !activeRoom) return null;

  const unitPrice = activeRoom[DURATION_UNIT_PRICE_KEY[unit]];
  const priceAvailable = unitPrice != null;
  const dur = Math.max(1, Number(duration) || 1);
  const subtotal = priceAvailable ? unitPrice * dur : null;
  const deposit = activeRoom.deposit_amount || 0;
  const dpRate = activeRoom.dp_rate != null ? activeRoom.dp_rate : 100;
  // Discount ONLY reduces the room price; deposit stays separate.
  const discountAmount = promo ? promo.discount_amount : 0;
  const netRoom = promo ? promo.discounted_room_price_total : subtotal;
  const total = priceAvailable ? (netRoom + deposit) : null;
  const dpAmount = priceAvailable ? Math.round(netRoom * dpRate / 100) : null;
  const endDate = addPeriod(startDate, dur, unit);

  const clearPromo = () => { setPromo(null); setPromoError(""); };

  const applyPromo = async () => {
    const code = promoInput.trim();
    if (!code) { setPromoError("Masukkan kode promo."); return; }
    if (!priceAvailable) { setPromoError("Harga kamar belum tersedia untuk durasi ini."); return; }
    setPromoError("");
    setPromoLoading(true);
    try {
      const { data } = await apiClient.post("/promos/validate", {
        property_id: propertyId,
        room_id: activeRoom.id,
        promo_code: code,
        duration: dur,
        duration_unit: unit,
      });
      setPromo(data);
    } catch (err) {
      setPromo(null);
      setPromoError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setPromoLoading(false);
    }
  };

  const reset = () => {
    setResult(null); setError(""); setEmailExists(false); setSubmitting(false);
    setStartDate(todayISO()); setDuration(1); setUnit(initialUnit); setNotes("");
    setPromoInput(""); setPromo(null); setPromoError("");
    setGName(""); setGPhone(""); setGEmail("");
  };

  const close = () => { reset(); onClose?.(); };

  const submit = async () => {
    setError(""); setEmailExists(false);
    if (!startDate) { setError("Tanggal mulai wajib diisi."); return; }
    if (!priceAvailable) { setError("Harga untuk durasi ini belum tersedia pada kamar."); return; }
    if (guestMode) {
      if (gName.trim().length < 2) { setError("Nama wajib diisi."); return; }
      if (gPhone.trim().length < 6) { setError("Nomor WhatsApp/HP tidak valid."); return; }
      if (!/^\S+@\S+\.\S+$/.test(gEmail.trim())) { setError("Email tidak valid."); return; }
    }
    setSubmitting(true);
    try {
      if (guestMode) {
        const { data } = await apiClient.post("/public/guest-booking", {
          property_id: propertyId,
          room_id: activeRoom.id,
          start_date: startDate,
          duration: dur,
          duration_unit: unit,
          notes: notes.trim() || null,
          promo_code: promo ? promo.promo_code : null,
          name: gName.trim(),
          phone: gPhone.trim(),
          email: gEmail.trim().toLowerCase(),
        });
        await loginWithToken(data.access_token);
        setResult(data.booking);
        onSuccess?.(data.booking);
      } else {
        const { data } = await apiClient.post("/bookings", {
          property_id: propertyId,
          room_id: activeRoom.id,
          start_date: startDate,
          duration: dur,
          duration_unit: unit,
          notes: notes.trim() || null,
          promo_code: promo ? promo.promo_code : null,
        });
        setResult(data);
        onSuccess?.(data);
      }
    } catch (err) {
      if (err?.response?.status === 409 && guestMode) setEmailExists(true);
      setError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center p-4" data-testid="booking-modal">
      <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={close} />
      <div className="relative w-full max-w-lg bg-card border border-border rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-card rounded-t-2xl">
          <h3 className="font-heading text-lg font-semibold">
            {result ? "Booking Terkirim" : "Booking Kamar"}
          </h3>
          <button onClick={close} data-testid="booking-modal-close" className="p-1.5 rounded-lg hover:bg-secondary/60 transition-colors">
            <X size={18} />
          </button>
        </div>

        {result ? (
          <div className="p-6 text-center" data-testid="booking-success">
            <span className="grid place-items-center w-14 h-14 rounded-2xl bg-emerald-100 text-emerald-600 mx-auto mb-4">
              <CheckCircle2 size={28} />
            </span>
            <h4 className="font-heading text-xl font-bold">Permintaan booking terkirim!</h4>
            <p className="mt-2 text-sm text-muted-foreground">
              Status: <span className="font-semibold text-amber-600">Menunggu persetujuan pemilik</span>.
            </p>
            <div className="mt-5 text-left rounded-xl border border-border bg-secondary/30 p-4 space-y-1.5 text-sm">
              <Row label="Kamar" value={`${result.room_number}${result.room_name ? " · " + result.room_name : ""}`} />
              <Row label="Mulai" value={formatDate(result.start_date)} />
              <Row label="Selesai" value={formatDate(result.end_date)} />
              <Row label="Total" value={<span className="font-semibold text-primary">{formatRupiah(result.total_amount)}</span>} />
            </div>
            <button onClick={close} data-testid="booking-success-close"
              className="mt-6 w-full py-3 rounded-full bg-primary text-primary-foreground font-semibold hover:scale-[1.02] active:scale-95 transition-transform">
              Selesai
            </button>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            <div className="rounded-xl bg-secondary/40 px-4 py-3">
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">Kamar</p>
              {roomChoices.length > 1 ? (
                <select value={roomId} onChange={(e) => { setRoomId(e.target.value); clearPromo(); }}
                  data-testid="booking-room-select"
                  className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm font-semibold outline-none focus:ring-2 focus:ring-primary/30">
                  {roomChoices.map((r) => (
                    <option key={r.id} value={r.id}>{r.room_number}{r.name ? ` · ${r.name}` : ""}</option>
                  ))}
                </select>
              ) : (
                <p className="mt-0.5 text-sm font-semibold">{activeRoom.room_number}{activeRoom.name ? ` · ${activeRoom.name}` : ""}</p>
              )}
              {propertyName && <p className="text-xs text-muted-foreground mt-1">{propertyName}</p>}
            </div>

            {guestMode && (
              <div className="rounded-xl border border-primary/25 bg-primary/5 p-4 space-y-3" data-testid="booking-guest-fields">
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-primary flex items-center gap-1.5">
                  <UserPlus size={14} /> Data Calon Penghuni
                </p>
                <p className="text-xs text-muted-foreground -mt-1">Tanpa perlu daftar akun. Isi data Anda untuk memulai booking.</p>
                <div>
                  <label className="block text-sm font-medium mb-1">Nama Lengkap</label>
                  <input value={gName} onChange={(e) => setGName(e.target.value)} data-testid="booking-guest-name"
                    className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30" placeholder="Nama Anda" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">No. WhatsApp/HP</label>
                    <input value={gPhone} onChange={(e) => setGPhone(e.target.value)} data-testid="booking-guest-phone"
                      className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30" placeholder="08xxxxxxxxxx" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Email</label>
                    <input type="email" value={gEmail} onChange={(e) => setGEmail(e.target.value)} data-testid="booking-guest-email"
                      className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30" placeholder="email@contoh.com" />
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1.5">Tanggal Mulai</label>
                <input type="date" value={startDate} min={todayISO()} onChange={(e) => setStartDate(e.target.value)}
                  data-testid="booking-start-date"
                  className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Durasi</label>
                <input type="number" min={1} value={duration} onChange={(e) => { setDuration(e.target.value); clearPromo(); }}
                  data-testid="booking-duration"
                  className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5">Satuan Durasi</label>
              <div className="flex gap-2" data-testid="booking-unit">
                {UNIT_OPTIONS.map((o) => (
                  <button key={o.value} type="button" onClick={() => { setUnit(o.value); clearPromo(); }}
                    data-testid={`booking-unit-${o.value}`}
                    className={`flex-1 py-2 rounded-xl text-sm font-medium border transition-colors ${
                      unit === o.value ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-secondary/60"
                    }`}>
                    {o.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5">Catatan <span className="text-muted-foreground font-normal">(opsional)</span></label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                data-testid="booking-notes"
                className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="Contoh: mulai pagi hari" />
            </div>

            {/* Promo code (Step 25) */}
            <div data-testid="booking-promo-section">
              <label className="block text-sm font-medium mb-1.5">Kode Promo <span className="text-muted-foreground font-normal">(opsional)</span></label>
              {promo ? (
                <div className="flex items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3" data-testid="booking-promo-applied">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-emerald-700 flex items-center gap-1.5"><Tag size={14} /> {promo.promo_code}</p>
                    <p className="text-xs text-emerald-600 truncate">{promo.promo_name} · hemat {formatRupiah(promo.discount_amount)}</p>
                  </div>
                  <button type="button" onClick={clearPromo} data-testid="booking-promo-remove"
                    className="text-xs font-semibold text-emerald-700 hover:underline shrink-0">Hapus</button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input value={promoInput} onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                    data-testid="booking-promo-input" placeholder="PROMO10"
                    className="flex-1 rounded-xl border border-border bg-background px-3 py-2.5 text-sm uppercase outline-none focus:ring-2 focus:ring-primary/30" />
                  <button type="button" onClick={applyPromo} disabled={promoLoading || !priceAvailable}
                    data-testid="booking-promo-apply"
                    className="px-4 py-2.5 rounded-xl text-sm font-semibold bg-primary text-primary-foreground hover:scale-[1.03] active:scale-95 transition-transform disabled:opacity-60 flex items-center gap-1.5">
                    {promoLoading && <Loader2 size={14} className="animate-spin" />} Gunakan
                  </button>
                </div>
              )}
              {promoError && <p className="mt-1.5 text-xs text-destructive" data-testid="booking-promo-error">{promoError}</p>}
            </div>

            {/* Summary */}
            <div className="rounded-xl border border-border p-4 space-y-1.5 text-sm" data-testid="booking-summary">
              <div className="flex items-center gap-2 mb-1 text-muted-foreground">
                <CalendarDays size={15} /> <span className="text-xs font-bold uppercase tracking-[0.15em]">Ringkasan</span>
              </div>
              <Row label="Periode" value={`${formatDate(startDate)} → ${formatDate(endDate)}`} />
              <Row label={`Harga / ${DURATION_UNIT_LABELS[unit]}`} value={priceAvailable ? formatRupiah(unitPrice) : "Tidak tersedia"} />
              <Row label={`Harga Kamar (${dur} ${DURATION_UNIT_LABELS[unit]})`} value={priceAvailable ? formatRupiah(subtotal) : "-"} />
              {promo && (
                <>
                  <Row label={<span className="text-emerald-600">Diskon {promo.promo_code}</span>}
                    value={<span className="text-emerald-600 font-semibold" data-testid="booking-summary-discount">-{formatRupiah(discountAmount)}</span>} />
                  <Row label="Harga Kamar Setelah Diskon"
                    value={<span className="font-semibold" data-testid="booking-summary-net-room">{formatRupiah(netRoom)}</span>} />
                </>
              )}
              <Row label="Deposit" value={formatRupiah(deposit)} />
              <div className="pt-2 mt-1 border-t border-border">
                <Row label={<span className="font-semibold">Total Kewajiban</span>}
                  value={<span className="font-heading text-lg font-bold text-primary" data-testid="booking-summary-total">{priceAvailable ? formatRupiah(total) : "-"}</span>} />
              </div>
              {priceAvailable && (
                <Row
                  label={<span className="text-xs">{dpRate < 100 ? `Pembayaran awal (DP ${dpRate}%)` : "Pembayaran awal (harga kamar penuh)"}</span>}
                  value={<span className="text-xs font-bold text-primary" data-testid="booking-summary-dp">{formatRupiah(dpAmount)}</span>}
                />
              )}
              <p className="pt-1 text-xs text-muted-foreground">Pembayaran dilakukan setelah booking disetujui pemilik. Deposit dibayar terpisah dari harga kamar.</p>
            </div>

            {error && (
              <div className="rounded-xl bg-destructive/10 border border-destructive/30 px-4 py-3 text-sm text-destructive" data-testid="booking-error">
                {error}
                {emailExists && (
                  <Link to="/login" data-testid="booking-guest-login-link"
                    className="mt-2 inline-flex items-center gap-1.5 font-semibold text-primary hover:underline">
                    Masuk ke akun Anda →
                  </Link>
                )}
              </div>
            )}

            <button onClick={submit} disabled={submitting || !priceAvailable}
              data-testid="booking-submit"
              className="w-full py-3 rounded-full bg-accent text-accent-foreground font-semibold hover:scale-[1.02] active:scale-95 transition-transform disabled:opacity-60 disabled:hover:scale-100 flex items-center justify-center gap-2">
              {submitting && <Loader2 size={16} className="animate-spin" />}
              {submitting ? "Memproses..." : "Konfirmasi Booking"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground text-right">{value}</span>
    </div>
  );
}
